<script setup>
import {computed, ref, watch, nextTick} from 'vue'
import {useUserStore} from '@/stores/user'
import {useItemDataStore} from '@/stores/itemData'
import {useViewStore} from '@/stores/view'
import {useRoute, useRouter} from 'vue-router'

import UserTrackingAvatar from '@/common/UserTrackingAvatar.vue'
import TableCell from '@/common/table-cell/TableCell.vue'

const props = defineProps({
    viewId: String,
    id: Number,
})

const viewStore = useViewStore(props.viewId)

const router = useRouter()
const route = useRoute()

const userStore = useUserStore()
const itemDataStore = useItemDataStore()

const show = ref(true)
const item = ref(undefined)

const tableName = computed(() => viewStore.tableName)
const selectedIds = computed(() => viewStore.selectedIds || [])
const selectMode = computed(() => viewStore.selectMode)
const columns = computed(() => viewStore.visibleColumnIds)

const isDarkMode = computed(() => {
    return userStore.pref('darkMode')
})

const cellBgColour = computed(() => {
    const className = isDarkMode.value ? 'text-bg-secondary' : 'text-bg-warning'
    return selectedIds.value.indexOf(props.id) > -1 ? className : ''
})

watch(
    () => [tableName.value, props.id],
    async ([newTable, newId]) => {
        if (!newTable || !newId) {
            item.value = undefined
            return
        }

        item.value = await itemDataStore.get({
            tableName: newTable,
            id: newId
        })
    },
    {immediate: true}
)

// Re-trigger row rendering on item state change
watch(item, () => {
    show.value = false
    nextTick(() => {
        show.value = true
    })
})

const onClick = () => {
    viewStore.onClick(props.id)
}

const onDblClick = () => {
    const hasAccess = viewStore.onDblClick()
    if (hasAccess) {
        router.push({
            name: 'form',
            params: {id: props.id},
            query: route.query
        })
    }
}

const onShiftClick = () => {
    viewStore.onShiftClick(props.id)
}

const onMetaClick = () => {
    viewStore.onMetaClick(props.id)
}
</script>

<template>
    <tr v-if="show" :class="{ 'table-active': selectedIds.indexOf(id) > -1 }" @dblclick="onDblClick"
        @click.left.exact="onClick" @click.meta="onMetaClick"
        @click.shift="onShiftClick">

        <td v-if="!selectMode" class="table-cell-select" :class="cellBgColour">
            <small
                :class="{ 'bi-square': selectedIds.indexOf(id) === -1, 'bi-check-square': selectedIds.indexOf(id) > -1 }"
                @click.stop.left.exact="onMetaClick" @click.stop.shift="onShiftClick"/>
        </td>

        <td v-if="item" data-name="id" :class="cellBgColour">
            {{ item.id }}

            <UserTrackingAvatar :item-id="id" :table-name="tableName"/>
        </td>

        <TableCell :class="cellBgColour" v-for="columnId in columns" :id="id" :key="columnId"
                   :column-id="columnId" :view-id="viewId"/>
    </tr>
</template>